/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Linking,
  Platform,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors } from '../../shared/theme/colors'
import { spacing } from '../../shared/theme/spacing';
import { settingsStyles } from '../../settings/screens/settingsScreen.styles';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';
import { showInfo, showSuccess } from '../../shared/ui/appToast';
import { saleDetailStyles as styles } from '../../sales/components/saleDetail.styles';
import { formatInvoiceCaeLine, resolveFiscalInvoiceDisplayStatus } from '../../sales/application/salePresentation';
import { formatMoney } from '../../shared/format/public-api';
import {
  consultFiscalInvoiceInAfip,
  emitFiscalDebitNote,
  fetchCreditNoteForSale,
  fetchDebitNotesForSale,
  fetchFiscalInvoiceForSale,
  retryFiscalCreditNoteEmit,
  retryFiscalInvoiceEmit,
} from '../infra/fiscalRepository'
import type { FiscalInvoiceConsultResult, FiscalInvoiceDetailRow, FiscalInvoiceEmitResult } from '../infra/fiscalRepository'
import { copyFiscalComprobanteSummary, downloadFiscalComprobantePdf } from '../application/fiscalInvoiceExport'
import type { FiscalComprobanteExportInput } from '../application/fiscalInvoiceExport'
import { buildAfipQrUrl, formatComprobanteNumber } from '../application/fiscalComprobanteFormat';
import { resolveInvoiceType, spanishInvoiceTypeLabel } from '../domain/invoiceTypes';
import { getFiscalContext } from '../infra/fiscalConfigRepo';
import { printFiscalComprobante } from '../../printing/public-api';

export type SaleFiscalComprobantePanelProps = {
  saleId: string;
  branchId: string;
  fiscalMode: string;
  fiscalStatus: string;
  saleStatus?: string;
  invoiceStatus?: string | null;
  invoiceCae?: string | null;
  invoiceIssuedAt?: string | null;
  invoiceQrUrl?: string | null;
  exportContext?: FiscalComprobanteExportInput | null;
  emitterFiscalEnvironment?: string | null;
  creditNoteStatus?: string | null;
  creditNoteCae?: string | null;
  creditNoteIssuedAt?: string | null;
  canRetry?: boolean;
  onRetryComplete?: () => void;
};

function formatConsultSummary(result: FiscalInvoiceConsultResult): string {
  const lines = [
    `Ambiente: ${result.environment ?? '—'}`,
    `CAE ARCA: ${result.afip?.cae ?? '—'}`,
    `CAE Vuelto: ${result.stored?.cae ?? '—'}`,
    `Match CAE: ${result.matches?.cae === true ? 'Sí' : result.matches?.cae === false ? 'No' : '—'}`,
    `Importe ARCA: ${result.afip?.impTotal ?? '—'}`,
    `Importe Vuelto: ${result.stored?.totalAmount ?? '—'}`,
    `Match importe: ${result.matches?.importe === true ? 'Sí' : result.matches?.importe === false ? 'No' : '—'}`,
  ];
  return lines.join('\n');
}

export function SaleFiscalComprobantePanel({
  saleId,
  branchId,
  fiscalMode,
  fiscalStatus,
  saleStatus,
  invoiceStatus,
  invoiceCae,
  invoiceIssuedAt,
  invoiceQrUrl,
  exportContext,
  canRetry = true,
  emitterFiscalEnvironment,
  creditNoteStatus,
  creditNoteCae,
  creditNoteIssuedAt,
  onRetryComplete,
}: SaleFiscalComprobantePanelProps) {
  const [retrying, setRetrying] = useState(false);
  const [consulting, setConsulting] = useState(false);
  const [ncRetrying, setNcRetrying] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [thermalPrinting, setThermalPrinting] = useState(false);
  const [ncPrinting, setNcPrinting] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [emitOverride, setEmitOverride] = useState<FiscalInvoiceEmitResult | null>(null);
  const [ncCaeOverride, setNcCaeOverride] = useState<string | null>(null);
  const [debitNotes, setDebitNotes] = useState<FiscalInvoiceDetailRow[]>([]);
  const [ndFormOpen, setNdFormOpen] = useState(false);
  const [ndAmountText, setNdAmountText] = useState('');
  const [ndEmitting, setNdEmitting] = useState(false);
  const [ndError, setNdError] = useState<string | null>(null);
  const [ndPrintingId, setNdPrintingId] = useState<string | null>(null);

  useEffect(() => {
    setEmitOverride(null);
    setNcCaeOverride(null);
  }, [saleId, fiscalStatus, invoiceStatus, invoiceCae, invoiceIssuedAt, invoiceQrUrl, creditNoteCae]);

  const effectiveFiscalStatus =
    emitOverride?.status === 'approved' || emitOverride?.cae?.trim()
      ? 'approved'
      : fiscalStatus;
  const effectiveInvoiceStatus =
    emitOverride?.status === 'approved' || emitOverride?.cae?.trim()
      ? 'approved'
      : invoiceStatus;
  const effectiveInvoiceCae = emitOverride?.cae?.trim() || invoiceCae;
  const effectiveInvoiceIssuedAt = emitOverride?.issuedAt ?? invoiceIssuedAt;
  const effectiveInvoiceQrUrl = emitOverride?.qrUrl ?? invoiceQrUrl;
  const effectiveCreditNoteCae = ncCaeOverride ?? creditNoteCae;

  const canEmitDebitNote =
    canRetry && saleStatus !== 'voided' && Boolean(effectiveInvoiceCae?.trim());

  const reloadDebitNotes = async () => {
    const rows = await fetchDebitNotesForSale(saleId);
    setDebitNotes(rows);
  };

  useEffect(() => {
    if (canEmitDebitNote) void reloadDebitNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saleId, canEmitDebitNote]);

  const exportInput: FiscalComprobanteExportInput | null =
    exportContext && effectiveInvoiceCae?.trim()
      ? {
          ...exportContext,
          cae: effectiveInvoiceCae,
          issuedAt: effectiveInvoiceIssuedAt,
          qrUrl: effectiveInvoiceQrUrl,
          // El retry en-sesión trae numeración/CAE/QR frescos — mergearlos para
          // que PDF e impresión reflejen la emisión sin recargar el detalle.
          caeExpiration: emitOverride?.caeExpiration ?? exportContext.caeExpiration,
          invoiceType: emitOverride?.invoiceType ?? exportContext.invoiceType,
          pointOfSale: emitOverride?.pointOfSale ?? exportContext.pointOfSale,
          invoiceNumber: emitOverride?.invoiceNumber ?? exportContext.invoiceNumber,
          netAmount: emitOverride?.netAmount ?? exportContext.netAmount,
          ivaAmount: emitOverride?.ivaAmount ?? exportContext.ivaAmount,
          buyerName: emitOverride?.buyerName ?? exportContext.buyerName,
          buyerCuit: emitOverride?.buyerCuit ?? exportContext.buyerCuit,
        }
      : null;

  if (fiscalMode === 'non_fiscal') return null;

  const statusLabel = resolveFiscalInvoiceDisplayStatus({
    fiscalStatus: effectiveFiscalStatus,
    invoiceStatus: effectiveInvoiceStatus,
    invoiceCae: effectiveInvoiceCae,
  });
  const caeLine = formatInvoiceCaeLine(effectiveInvoiceCae, effectiveInvoiceIssuedAt);
  const showRetry =
    canRetry &&
    saleStatus !== 'voided' &&
    (effectiveFiscalStatus === 'pending_sync' ||
      effectiveInvoiceStatus === 'pending' ||
      effectiveInvoiceStatus === 'pending_retry' ||
      effectiveFiscalStatus === 'pending_retry' ||
      !effectiveInvoiceCae?.trim());

  const showNcRetry =
    canRetry &&
    saleStatus === 'voided' &&
    (creditNoteStatus === 'pending' ||
      creditNoteStatus === 'pending_retry' ||
      creditNoteStatus === 'error') &&
    !effectiveCreditNoteCae?.trim();

  const canExport = Boolean(exportInput);
  const qrVerificationUrl = buildAfipQrUrl(
    exportContext?.qrPayload ?? null,
    effectiveInvoiceQrUrl,
    {
      buyerCuit: exportContext?.buyerCuit ?? null,
      cae: effectiveInvoiceCae,
    }
  );
  const isHomologacion =
    emitterFiscalEnvironment?.trim().toLowerCase() === 'homologacion' ||
    emitterFiscalEnvironment?.trim().toLowerCase() === 'homologación';
  const canConstatar = Boolean(effectiveInvoiceCae?.trim());

  const runExport = async (action: 'downloadPdf' | 'copy') => {
    if (!exportInput) return;
    setExportBusy(true);
    try {
      if (action === 'downloadPdf') await downloadFiscalComprobantePdf(exportInput);
      else await copyFiscalComprobanteSummary(exportInput);
    } catch (err: unknown) {
      setRetryError(err instanceof Error ? err.message : 'No se pudo exportar el comprobante.');
    } finally {
      setExportBusy(false);
    }
  };

  const handleOpenQr = () => {
    if (qrVerificationUrl) void Linking.openURL(qrVerificationUrl);
  };

  const handleConsultAfip = async () => {
    setConsulting(true);
    setRetryError(null);
    try {
      const result = await consultFiscalInvoiceInAfip(saleId, branchId);
      showInfo('Constatación ARCA', formatConsultSummary(result));
    } catch (err: unknown) {
      setRetryError(err instanceof Error ? err.message : 'No se pudo constatar en ARCA.');
    } finally {
      setConsulting(false);
    }
  };

  const handleRetry = async () => {
    setRetrying(true);
    setRetryError(null);
    try {
      const result = await retryFiscalInvoiceEmit(saleId, branchId);
      setEmitOverride(result);
      onRetryComplete?.();
    } catch (err: unknown) {
      setRetryError(err instanceof Error ? err.message : 'No se pudo reintentar.');
    } finally {
      setRetrying(false);
    }
  };

  /** Emisor del perfil fiscal (datos que AFIP pide en el comprobante impreso). */
  const loadEmitterInput = async () => {
    const ctx = await getFiscalContext(branchId).catch(() => null);
    const profile = ctx?.profile;
    return {
      emitter: {
        businessName: profile?.businessName ?? exportContext?.emitterBusinessName,
        cuit: profile?.emitterCuit ?? exportContext?.emitterCuit,
        taxRegime: profile?.taxRegime ?? null,
        domicilioComercial: profile?.domicilioComercial,
        iibb: profile?.iibb,
        inicioActividades: profile?.inicioActividades,
      },
      environment: profile?.environment ?? emitterFiscalEnvironment ?? null,
    };
  };

  /** Imprime la Factura A/B/C por la térmica (CAE + QR AFIP). */
  const handlePrintThermal = async () => {
    if (!exportInput) return;
    setThermalPrinting(true);
    setRetryError(null);
    try {
      const invoice = await fetchFiscalInvoiceForSale(saleId);
      const { emitter, environment } = await loadEmitterInput();
      await printFiscalComprobante({
        branchId,
        input: {
          invoiceType: invoice?.invoiceType ?? exportInput.invoiceType,
          pointOfSale: invoice?.pointOfSale ?? exportInput.pointOfSale,
          invoiceNumber: invoice?.invoiceNumber ?? exportInput.invoiceNumber,
          cae: effectiveInvoiceCae?.trim() ?? '',
          caeExpiration: invoice?.caeExpiration ?? exportInput.caeExpiration,
          issuedAt: invoice?.issuedAt ?? exportInput.issuedAt,
          qrUrl: invoice ? null : effectiveInvoiceQrUrl,
          qrPayload: invoice?.qrPayload ?? exportContext?.qrPayload,
          buyerName: invoice?.buyerName ?? exportInput.buyerName,
          buyerCuit: invoice?.buyerCuit ?? exportInput.buyerCuit,
          buyerTaxCondition: invoice?.buyerTaxCondition,
          items: exportInput.items,
          total: invoice?.totalAmount ?? exportInput.saleTotal,
          netAmount: invoice?.netAmount ?? exportInput.netAmount,
          ivaAmount: invoice?.ivaAmount ?? exportInput.ivaAmount,
          saleRef: exportInput.saleRef,
          diningTableName: exportInput.diningTableName,
          copyLabel: 'DUPLICADO',
          emitter,
          environment,
        },
      });
    } catch (err: unknown) {
      setRetryError(err instanceof Error ? err.message : 'No se pudo imprimir el comprobante.');
    } finally {
      setThermalPrinting(false);
    }
  };

  /** Imprime la Nota de crédito emitida por la térmica. */
  const handlePrintCreditNote = async () => {
    setNcPrinting(true);
    setRetryError(null);
    try {
      const nc = await fetchCreditNoteForSale(saleId);
      const ncCae = effectiveCreditNoteCae?.trim() || nc?.cae?.trim() || '';
      if (!nc?.cae && !ncCae) {
        throw new Error('La nota de crédito todavía no está emitida.');
      }
      const { emitter, environment } = await loadEmitterInput();
      const originalNro = formatComprobanteNumber(
        exportContext?.pointOfSale,
        exportContext?.invoiceNumber
      );
      const originalLabel = exportContext?.invoiceType
        ? spanishInvoiceTypeLabel(exportContext.invoiceType).toUpperCase()
        : null;
      await printFiscalComprobante({
        branchId,
        input: {
          invoiceType:
            nc?.invoiceType ??
            resolveInvoiceType({
              emitterRegime:
                emitter.taxRegime === 'responsable_inscripto'
                  ? 'responsable_inscripto'
                  : 'monotributo',
              isCreditNote: true,
              originalInvoiceType: exportContext?.invoiceType,
            }),
          pointOfSale: nc?.pointOfSale,
          invoiceNumber: nc?.invoiceNumber,
          cae: nc?.cae ?? ncCae,
          caeExpiration: nc?.caeExpiration,
          issuedAt: nc?.issuedAt ?? creditNoteIssuedAt,
          qrPayload: nc?.qrPayload,
          buyerName: nc?.buyerName ?? exportContext?.buyerName,
          buyerCuit: nc?.buyerCuit ?? exportContext?.buyerCuit,
          buyerTaxCondition: nc?.buyerTaxCondition,
          items: exportContext?.items ?? [],
          total: nc?.totalAmount ?? exportContext?.saleTotal ?? 0,
          netAmount: nc?.netAmount,
          ivaAmount: nc?.ivaAmount,
          saleRef: exportContext?.saleRef,
          copyLabel: 'DUPLICADO',
          relatedDocLabel:
            originalLabel && originalNro ? `Anula ${originalLabel} ${originalNro}` : null,
          emitter,
          environment,
        },
      });
    } catch (err: unknown) {
      setRetryError(err instanceof Error ? err.message : 'No se pudo imprimir la nota de crédito.');
    } finally {
      setNcPrinting(false);
    }
  };

  const handleRetryCreditNote = async () => {
    setNcRetrying(true);
    setRetryError(null);
    try {
      const result = await retryFiscalCreditNoteEmit(saleId);
      if (result.cae?.trim()) setNcCaeOverride(result.cae.trim());
      onRetryComplete?.();
    } catch (err: unknown) {
      setRetryError(err instanceof Error ? err.message : 'No se pudo reintentar la NC.');
    } finally {
      setNcRetrying(false);
    }
  };

  const parseNdAmount = (): number | null => {
    const normalized = ndAmountText.replace(',', '.').trim();
    const parsed = Number(normalized);
    return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) / 100 : null;
  };

  const handleEmitDebitNote = async () => {
    const amount = parseNdAmount();
    if (amount == null) {
      setNdError('Ingresá un monto mayor a 0.');
      return;
    }
    setNdEmitting(true);
    setNdError(null);
    try {
      const pendingNd = debitNotes.find((n) => n.status === 'pending_retry');
      const result = await emitFiscalDebitNote(saleId, amount, null, pendingNd?.id ?? null);
      if (result.error) {
        setNdError(
          result.status === 'pending_retry'
            ? 'No se pudo emitir. Quedó pendiente de reintento automático.'
            : result.error
        );
        await reloadDebitNotes();
        return;
      }
      showSuccess('Nota de débito emitida', 'El comprobante ya tiene CAE.');
      setNdAmountText('');
      setNdFormOpen(false);
      await reloadDebitNotes();
      onRetryComplete?.();
    } catch (err: unknown) {
      setNdError(err instanceof Error ? err.message : 'No se pudo emitir la nota de débito.');
      await reloadDebitNotes().catch(() => undefined);
    } finally {
      setNdEmitting(false);
    }
  };

  /** Imprime una Nota de débito emitida por la térmica. */
  const handlePrintDebitNote = async (nd: FiscalInvoiceDetailRow) => {
    setNdPrintingId(nd.id);
    setNdError(null);
    try {
      const { emitter, environment } = await loadEmitterInput();
      const originalNro = formatComprobanteNumber(
        exportContext?.pointOfSale,
        exportContext?.invoiceNumber
      );
      const originalLabel = exportContext?.invoiceType
        ? spanishInvoiceTypeLabel(exportContext.invoiceType).toUpperCase()
        : null;
      await printFiscalComprobante({
        branchId,
        input: {
          invoiceType: nd.invoiceType,
          pointOfSale: nd.pointOfSale,
          invoiceNumber: nd.invoiceNumber,
          cae: nd.cae ?? '',
          caeExpiration: nd.caeExpiration,
          issuedAt: nd.issuedAt,
          qrPayload: nd.qrPayload,
          buyerName: nd.buyerName ?? exportContext?.buyerName,
          buyerCuit: nd.buyerCuit ?? exportContext?.buyerCuit,
          buyerTaxCondition: nd.buyerTaxCondition,
          items: [],
          total: nd.totalAmount ?? 0,
          netAmount: nd.netAmount,
          ivaAmount: nd.ivaAmount,
          saleRef: exportContext?.saleRef,
          copyLabel: 'DUPLICADO',
          relatedDocLabel:
            originalLabel && originalNro ? `Ref ${originalLabel} ${originalNro}` : null,
          emitter,
          environment,
        },
      });
    } catch (err: unknown) {
      setNdError(err instanceof Error ? err.message : 'No se pudo imprimir la nota de débito.');
    } finally {
      setNdPrintingId(null);
    }
  };

  const ncStatusLabel =
    effectiveCreditNoteCae?.trim()
      ? 'Nota de crédito emitida'
      : creditNoteStatus === 'pending' || creditNoteStatus === 'pending_retry'
        ? 'Nota de crédito pendiente'
        : creditNoteStatus === 'error'
          ? 'Error al emitir NC'
          : creditNoteStatus === 'skipped'
            ? 'NC no requerida'
            : null;

  return (
    <>
      <Text style={styles.sectionTitle}>Comprobante fiscal</Text>
      <View style={styles.saleDetailSection}>
        <View style={styles.fiscalComprobanteRow}>
          <Ionicons name="receipt-outline" size={20} color={colors.accent} />
          <Text style={styles.fiscalComprobanteStatus}>{statusLabel}</Text>
        </View>
        {caeLine ? <Text style={styles.fiscalComprobanteCae}>{caeLine}</Text> : null}
        {isHomologacion && effectiveInvoiceCae?.trim() ? (
          <View style={styles.customerBanner}>
            <Text style={styles.customerBannerText}>
              Comprobante de prueba — sin validez legal. El portal público de ARCA no valida homologación.
            </Text>
          </View>
        ) : null}
        <View style={styles.fiscalComprobanteActions}>
          {!isHomologacion && qrVerificationUrl ? (
            <TouchableOpacity style={styles.fiscalActionBtn} onPress={handleOpenQr} accessibilityRole="link" testID="fiscal.comprobante.qr-afip">
              <Ionicons name="qr-code-outline" size={18} color={colors.accent} />
              <Text style={styles.fiscalActionText}>Ver QR ARCA</Text>
            </TouchableOpacity>
          ) : null}
          {canConstatar ? (
            <TouchableOpacity
              style={styles.fiscalActionBtn}
              onPress={() => void handleConsultAfip()}
              disabled={consulting}
              accessibilityRole="button"
            >
              {consulting ? (
                <ActivityIndicator size="small" color={colors.accent} />
              ) : (
                <>
                  <Ionicons name="shield-checkmark-outline" size={18} color={colors.accent} />
                  <Text style={styles.fiscalActionText}>
                    {isHomologacion ? 'Constatar en ARCA (homologación)' : 'Constatar en ARCA'}
                  </Text>
                </>
              )}
            </TouchableOpacity>
          ) : null}
          {canExport ? (
            <TouchableOpacity
              style={styles.fiscalActionBtn}
              onPress={() => void handlePrintThermal()}
              disabled={thermalPrinting}
              accessibilityRole="button"
              testID="fiscal.comprobante.print-thermal"
            >
              {thermalPrinting ? (
                <ActivityIndicator size="small" color={colors.accent} />
              ) : (
                <>
                  <Ionicons name="print-outline" size={18} color={colors.accent} />
                  <Text style={styles.fiscalActionText}>Imprimir</Text>
                </>
              )}
            </TouchableOpacity>
          ) : null}
          {canExport ? (
            <>
              <TouchableOpacity
                style={styles.fiscalActionBtn}
                onPress={() => void runExport('downloadPdf')}
                disabled={exportBusy}
                accessibilityRole="button"
                testID="fiscal.comprobante.download-pdf"
              >
                {exportBusy ? (
                  <ActivityIndicator size="small" color={colors.accent} />
                ) : (
                  <>
                    <Ionicons name="download-outline" size={18} color={colors.accent} />
                    <Text style={styles.fiscalActionText}>Descargar PDF</Text>
                  </>
                )}
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.fiscalActionBtn}
                onPress={() => void runExport('copy')}
                disabled={exportBusy}
                accessibilityRole="button"
              >
                <Ionicons name="copy-outline" size={18} color={colors.textPrimary} />
                <Text style={styles.fiscalActionText}>Copiar datos</Text>
              </TouchableOpacity>
            </>
          ) : null}
          {canExport && Platform.OS !== 'web' ? (
            <Text style={styles.caption}>
              En el celular podés guardar el PDF en Archivos o enviarlo por WhatsApp, Mail u otra app.
            </Text>
          ) : null}
          {showRetry ? (
            <TouchableOpacity
              style={styles.fiscalActionBtn}
              onPress={() => void handleRetry()}
              disabled={retrying}
              accessibilityRole="button"
            >
              {retrying ? (
                <ActivityIndicator size="small" color={colors.accent} />
              ) : (
                <>
                  <Ionicons name="refresh-outline" size={18} color={colors.accent} />
                  <Text style={styles.fiscalActionText}>Reintentar emisión</Text>
                </>
              )}
            </TouchableOpacity>
          ) : null}
        </View>
        {retryError ? <Text style={[styles.caption, { color: colors.danger, marginTop: spacing.xs }]}>{retryError}</Text> : null}
      </View>

      {saleStatus === 'voided' && ncStatusLabel ? (
        <>
          <Text style={styles.sectionTitle}>Nota de crédito</Text>
          <View style={styles.saleDetailSection}>
            <Text style={styles.fiscalComprobanteStatus}>{ncStatusLabel}</Text>
            {effectiveCreditNoteCae?.trim() ? (
              <Text style={styles.fiscalComprobanteCae}>
                {formatInvoiceCaeLine(effectiveCreditNoteCae, creditNoteIssuedAt)}
              </Text>
            ) : null}
            {effectiveCreditNoteCae?.trim() ? (
              <TouchableOpacity
                style={styles.fiscalActionBtn}
                onPress={() => void handlePrintCreditNote()}
                disabled={ncPrinting}
                accessibilityRole="button"
                testID="fiscal.credit-note.print-thermal"
              >
                {ncPrinting ? (
                  <ActivityIndicator size="small" color={colors.accent} />
                ) : (
                  <>
                    <Ionicons name="print-outline" size={18} color={colors.accent} />
                    <Text style={styles.fiscalActionText}>Imprimir NC</Text>
                  </>
                )}
              </TouchableOpacity>
            ) : null}
            {showNcRetry ? (
              <TouchableOpacity
                style={styles.fiscalActionBtn}
                onPress={() => void handleRetryCreditNote()}
                disabled={ncRetrying}
                accessibilityRole="button"
              >
                {ncRetrying ? (
                  <ActivityIndicator size="small" color={colors.accent} />
                ) : (
                  <>
                    <Ionicons name="refresh-outline" size={18} color={colors.accent} />
                    <Text style={styles.fiscalActionText}>Reintentar NC</Text>
                  </>
                )}
              </TouchableOpacity>
            ) : null}
          </View>
        </>
      ) : null}

      {canEmitDebitNote ? (
        <>
          <Text style={styles.sectionTitle}>Nota de débito</Text>
          <View style={styles.saleDetailSection}>
            {debitNotes.map((nd) => (
              <View key={nd.id} style={styles.fiscalComprobanteRow}>
                <Ionicons name="document-outline" size={18} color={colors.accent} />
                <Text style={styles.fiscalComprobanteStatus}>
                  {spanishInvoiceTypeLabel(nd.invoiceType)}{' '}
                  {nd.invoiceNumber != null
                    ? formatComprobanteNumber(nd.pointOfSale, nd.invoiceNumber)
                    : ''}
                  {nd.totalAmount != null ? ` · ${formatMoney(nd.totalAmount)}` : ''}
                </Text>
                {nd.cae?.trim() ? (
                  <TouchableOpacity
                    style={styles.fiscalActionBtn}
                    onPress={() => void handlePrintDebitNote(nd)}
                    disabled={ndPrintingId === nd.id}
                    accessibilityRole="button"
                  >
                    {ndPrintingId === nd.id ? (
                      <ActivityIndicator size="small" color={colors.accent} />
                    ) : (
                      <Ionicons name="print-outline" size={18} color={colors.accent} />
                    )}
                  </TouchableOpacity>
                ) : (
                  <Text style={styles.caption}>Pendiente</Text>
                )}
              </View>
            ))}
            {ndFormOpen ? (
              <View style={{ gap: spacing.xs }}>
                <Text style={styles.caption}>
                  Monto a debitar del comprobante {formatComprobanteNumber(
                    exportContext?.pointOfSale,
                    exportContext?.invoiceNumber
                  )}
                </Text>
                <NumericTextInput
                  kind="decimalMoney"
                  style={[settingsStyles.input, { minHeight: 44 }]}
                  value={ndAmountText}
                  onChangeText={setNdAmountText}
                  placeholder="Monto (ej. 1500,50)"
                  placeholderTextColor={colors.textSecondary}
                  testID="fiscal.debit-note.amount"
                />
                <View style={{ flexDirection: 'row', gap: spacing.xs }}>
                  <TouchableOpacity
                    style={styles.fiscalActionBtn}
                    onPress={() => void handleEmitDebitNote()}
                    disabled={ndEmitting}
                    accessibilityRole="button"
                    testID="fiscal.debit-note.emit"
                  >
                    {ndEmitting ? (
                      <ActivityIndicator size="small" color={colors.accent} />
                    ) : (
                      <>
                        <Ionicons name="checkmark-circle-outline" size={18} color={colors.accent} />
                        <Text style={styles.fiscalActionText}>Emitir ND</Text>
                      </>
                    )}
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.fiscalActionBtn}
                    onPress={() => {
                      setNdFormOpen(false);
                      setNdError(null);
                    }}
                    disabled={ndEmitting}
                    accessibilityRole="button"
                  >
                    <Ionicons name="close-outline" size={18} color={colors.textPrimary} />
                    <Text style={styles.fiscalActionText}>Cancelar</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ) : (
              <TouchableOpacity
                style={styles.fiscalActionBtn}
                onPress={() => setNdFormOpen(true)}
                accessibilityRole="button"
                testID="fiscal.debit-note.open"
              >
                <Ionicons name="add-circle-outline" size={18} color={colors.accent} />
                <Text style={styles.fiscalActionText}>Emitir nota de débito</Text>
              </TouchableOpacity>
            )}
            {ndError ? (
              <Text style={[styles.caption, { color: colors.danger, marginTop: spacing.xs }]}>
                {ndError}
              </Text>
            ) : null}
          </View>
        </>
      ) : null}
    </>
  );
}
